/**
 * SPONSOR ASSET DETAIL, the issuance lifecycle (UF-05 section 5.6, extended for the economic split).
 *
 * This is where the sponsor watches their asset move through the Trust and, once minted, manages their
 * own token allocation. The order of the page IS the lifecycle:
 *
 *   review pipeline  ->  valuation  ->  capital structure (approve)  ->  treasury  ->  offerings
 *
 * Two facts the page exists to make un-missable:
 *   1. The capital structure is GENERATED BY THE TRUST. The sponsor's only lever is Approve. They do not
 *      set the supply, the price, or the split; those come from the approved valuation.
 *   2. The treasury is the canonical split of the supply. Sponsor treasury, marketplace inventory,
 *      investor holdings, reserved, burned. An offering can only ever draw from the sponsor's own treasury.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type SponsorAsset } from '@/lib/api';
import { PageHeader, SectionTitle, StatTile } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDate } from '@/lib/format';

const num = (n: number) => n.toLocaleString('en-KE');

export default function AssetDetail() {
  const params = useParams();
  // React Router types a param as possibly absent; this route cannot match without one.
  const id = params.assetId ?? '';

  const [asset, setAsset] = useState<SponsorAsset | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setAsset(await api.sponsor.asset(id));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this asset.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function approveCapital() {
    setApproving(true);
    try {
      await api.sponsor.approveCapital(id);
      setAsset((a) =>
        a && a.capital ? { ...a, capital: { ...a.capital, approvedBySponsor: true } } : a,
      );
    } catch {
      /* surfaced on reload */
    } finally {
      setApproving(false);
    }
  }

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!asset) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/tokenize"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Tokenize
      </Link>

      <PageHeader
        title={asset.name}
        subtitle={`${asset.type} · ${asset.location}`}
        actions={
          <StatusPill
            status={asset.stage === 'offering' ? 'live' : asset.stage}
            label={STAGE_LABEL[asset.stage]}
          />
        }
      />

      {/* WHO HOLDS WHAT. The three-actor structure, stated plainly at the top so it is never a mystery. */}
      <section className="rounded-card border border-border bg-surface p-4 shadow-card">
        <div className="grid gap-3 sm:grid-cols-3">
          <Actor role="Sponsor" name="You" note="Originated the asset" />
          <Actor role="Holding entity" name={asset.trust.holdingEntity} note="Holds legal title" />
          <Actor
            role="Trust"
            name={asset.trust.name ?? 'Assigned after formation'}
            note={asset.trust.trustNumber ?? 'Issues and administers'}
          />
        </div>
      </section>

      {/* REVIEW PIPELINE. */}
      <section>
        <SectionTitle>Trust review</SectionTitle>
        <ol className="overflow-hidden rounded-card border border-border bg-surface shadow-card">
          {asset.review.map((step, i) => (
            <li
              key={step.key}
              className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-border' : ''}`}
            >
              <ReviewMark status={step.status} />
              <span className="flex-1 text-sm text-ink">{step.label}</span>
              <StatusPill status={step.status} />
            </li>
          ))}
        </ol>
      </section>

      {/* VALUATION. */}
      {asset.valuation ? (
        <Reveal>
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <SectionTitle>Valuation</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <StatTile label="Independent value" value={`KES ${asset.valuation.independent}`} />
              <StatTile label="Approved value" value={`KES ${asset.valuation.approved}`} accent />
            </div>
            <p className="mt-2 text-micro text-ink-subtle">
              {asset.valuation.valuer} · {fmtDate(asset.valuation.date)}
            </p>
          </section>
        </Reveal>
      ) : (
        <PendingCard
          title="Valuation"
          body="An independent valuer is assessing the asset. You will see the approved value here when it completes."
        />
      )}

      {/* CAPITAL STRUCTURE, generated by the Trust; the sponsor approves it. */}
      {asset.capital ? (
        <Reveal>
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <SectionTitle>Capital structure</SectionTitle>
            <p className="-mt-1 mb-3 text-micro text-ink-subtle">
              Generated by the trust from the approved value. You approve it; you do not set it.
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatTile label="Approved value" value={`KES ${asset.capital.approvedValue}`} />
              <StatTile label="Total supply" value={num(asset.capital.totalSupply)} />
              <StatTile label="Token price" value={`KES ${asset.capital.tokenPrice}`} />
              <StatTile label="Reserve" value={num(asset.capital.reserve)} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <StatTile
                label="Your allocation"
                value={num(asset.capital.sponsorAllocation)}
                accent
                sub={pct(asset.capital.sponsorAllocation, asset.capital.totalSupply)}
              />
              <StatTile
                label="Marketplace allocation"
                value={num(asset.capital.marketplaceAllocation)}
                sub={pct(asset.capital.marketplaceAllocation, asset.capital.totalSupply)}
              />
            </div>
            {asset.capital.approvedBySponsor ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-micro font-medium text-accent">
                <CheckIcon /> You approved this capital structure
              </p>
            ) : (
              <div className="mt-4 rounded-card bg-primary-tint p-3">
                <p className="text-sm text-ink">
                  Approve to authorize the trust to mint on these terms.
                </p>
                <button
                  onClick={approveCapital}
                  disabled={approving}
                  className="mt-2.5 w-full rounded-field bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-60"
                >
                  {approving ? 'Approving...' : 'Approve capital structure'}
                </button>
              </div>
            )}
          </section>
        </Reveal>
      ) : null}

      {/* TREASURY, the canonical split of the supply. */}
      {asset.treasury ? <TreasuryCard t={asset.treasury} /> : null}

      {/* OFFERINGS, drawn from the sponsor's own allocation, published only after Trust approval. */}
      {asset.capital?.approvedBySponsor ? (
        <section>
          <SectionTitle
            action={
              <Link
                to={`/tokenize/assets/${id}/offerings/new`}
                className="text-micro font-medium text-brand hover:underline"
              >
                Create offering
              </Link>
            }
          >
            Offerings
          </SectionTitle>
          {asset.offerings.length > 0 ? (
            <ul className="space-y-2.5">
              {asset.offerings.map((o) => (
                <li
                  key={o.id}
                  className="rounded-card border border-border bg-surface p-4 shadow-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-ink">{o.name}</p>
                      <p className="tabular text-micro text-ink-subtle">
                        {num(o.tokens)} tokens at KES {o.price}
                      </p>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                  {o.status === 'live' ? (
                    <div className="mt-3">
                      <div className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.round((o.sold / o.tokens) * 100)}%` }}
                        />
                      </div>
                      <p className="tabular mt-1 text-nano text-ink-subtle">
                        {num(o.sold)} of {num(o.tokens)} sold · closes {fmtDate(o.closes)}
                      </p>
                    </div>
                  ) : (
                    <p className="tabular mt-2 text-nano text-ink-subtle">
                      Opens {fmtDate(o.opens)} · closes {fmtDate(o.closes)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-card border border-dashed border-border bg-surface p-6 text-center">
              <p className="text-sm text-ink-muted">No offerings yet.</p>
              <p className="mt-1 text-micro text-ink-subtle">
                Bring part of your {num(asset.treasury?.sponsorTreasury ?? 0)}-token allocation to
                market when you are ready.
              </p>
              <Link
                to={`/tokenize/assets/${id}/offerings/new`}
                className="mt-3 inline-block rounded-field bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
              >
                Create your first offering
              </Link>
            </div>
          )}
        </section>
      ) : null}

      {/* FINANCING, the bridge into the credit marketplace. A pledged allocation can raise debt. */}
      {asset.capital?.approvedBySponsor ? (
        <section className="rounded-card border border-border bg-surface p-4 shadow-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-ink">Need capital without selling tokens?</p>
              <p className="text-micro text-ink-subtle">
                Raise financing against this asset in the credit marketplace.
              </p>
            </div>
            <Link
              to={`/credit/apply?asset=${id}`}
              className="shrink-0 rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
            >
              Request financing
            </Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}

const STAGE_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  diligence: 'In diligence',
  valuation: 'In valuation',
  formation: 'Trust formation',
  allocation: 'Allocating',
  offering: 'Live',
};

function pct(part: number, whole: number) {
  return whole > 0 ? `${Math.round((part / whole) * 100)}% of supply` : '-';
}

function Actor({ role, name, note }: { role: string; name: string; note: string }) {
  return (
    <div>
      <p className="text-nano uppercase tracking-wide text-ink-subtle">{role}</p>
      <p className="truncate text-sm font-medium text-ink">{name}</p>
      <p className="truncate text-micro text-ink-subtle">{note}</p>
    </div>
  );
}

function TreasuryCard({ t }: { t: NonNullable<SponsorAsset['treasury']> }) {
  const rows = [
    { label: 'Sponsor treasury', value: t.sponsorTreasury, tone: 'bg-primary' },
    { label: 'Marketplace inventory', value: t.marketplaceInventory, tone: 'bg-info' },
    { label: 'Investor holdings', value: t.investorHoldings, tone: 'bg-accent' },
    { label: 'Reserved', value: t.reserved, tone: 'bg-warning' },
    { label: 'Burned', value: t.burned, tone: 'bg-ink-subtle' },
  ].filter((r) => r.value > 0 || r.label === 'Sponsor treasury');

  return (
    <Reveal>
      <section className="rounded-card border border-border bg-surface p-5 shadow-card">
        <SectionTitle>Token treasury</SectionTitle>
        <p className="-mt-1 mb-3 text-micro text-ink-subtle">
          The canonical split of {num(t.total)} tokens. Every allocation, sale, transfer, and burn
          flows through here.
        </p>
        <div className="flex h-2.5 overflow-hidden rounded-full">
          {rows.map((r) => (
            <div
              key={r.label}
              className={r.tone}
              style={{ width: `${(r.value / t.total) * 100}%` }}
              title={`${r.label} ${num(r.value)}`}
            />
          ))}
        </div>
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li key={r.label} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-ink-muted">
                <span className={`h-2.5 w-2.5 rounded-sm ${r.tone}`} />
                {r.label}
              </span>
              <span className="tabular font-medium text-ink">{num(r.value)}</span>
            </li>
          ))}
        </ul>
      </section>
    </Reveal>
  );
}

function ReviewMark({ status }: { status: string }) {
  const done = status === 'verified' || status === 'complete' || status === 'approved';
  const active = status === 'in review';
  return (
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
        done
          ? 'bg-accent/15 text-accent'
          : active
            ? 'bg-warning/15 text-warning'
            : 'bg-surface-sunken text-ink-subtle'
      }`}
    >
      {done ? <CheckIcon /> : active ? <DotIcon /> : <DotIcon />}
    </span>
  );
}

function PendingCard({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-card border border-dashed border-border bg-surface p-5">
      <h2 className="text-section text-ink">{title}</h2>
      <p className="mt-1 text-sm text-ink-muted">{body}</p>
    </section>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="m5 13 4 4L19 7" />
    </svg>
  );
}
function DotIcon() {
  return (
    <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 8 8" aria-hidden>
      <circle cx="4" cy="4" r="4" />
    </svg>
  );
}
